import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {ChangeEvent} from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {TableEditBar} from '../../../components/TableEditBar';
import {WcvDocument} from '../../../graphql/documents.datatypes';
import {formatDate} from '../../../utils/formatters';
import {WcvType} from '../../../graphql/logs.datatypes';
import {getHost} from '../../../graphql/GraphQlProvider';
import uploadFile, {UploadFileVars} from '../../../graphql/uploadFile.api';
import {useSnackbar} from 'notistack';
import createDocument, {CreateDocumentInput} from '../createDocument.api';
import updateDocument, {UpdateDocumentInput} from '../updateDocument.api';
import {getMe} from '../../../graphql/auth';
import moment from 'moment';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {red} from '@material-ui/core/colors';
import {PaginatiorInfo} from '../../../graphql/common.datatypes';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'relative',
    },
    content: {
        padding: 0
    },
    inner: {
      [theme.breakpoints.up('md')]: {
        minWidth: 700,
      },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    linkButton: {
        marginRight: theme.spacing(1),
    },
    chipOverdue: {
        backgroundColor: red[200],
        marginLeft: theme.spacing(1),
    },
}));

const Results = (props: {
    className?: string,
    groupMemberId?: number,
    documents: WcvDocument[],
    isAdmin: boolean,
    paginatorInfo?: PaginatiorInfo,
    setPage?: (page: number) => void,
    rowsPerPage?: number,
    setRowsPerPage?: (rowsPerPage: number) => void,
}) => {
    const {className, documents, groupMemberId, isAdmin, paginatorInfo, setPage, rowsPerPage, setRowsPerPage, ...rest} = props;
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const meData = getMe();
    const [sendUploadFile] = uploadFile();
    const [sendCreateDocument] = createDocument();
    const [sendUpdateDocument] = updateDocument();

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        if(setPage) setPage(page + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if(setRowsPerPage) setRowsPerPage(Number(event.target.value));
    };

    const handleVerifyDocument = (document: WcvDocument,) => {
        const sanitisedData: UpdateDocumentInput = {
            data: {
                __typename: undefined,
                user: undefined,
                targetResource: undefined,
                targetGroup: undefined,
                targetGroupMember: undefined,
                completed_documents: undefined,
                overdueGroupMembers: undefined,

                id: document.id,
                verified: true,
            }
        };

        sendUpdateDocument({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Document Verified.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Verify Document Failed!', {variant: 'error'});
            });
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>, document: WcvDocument, memberDoc?: WcvDocument) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (file.size <= 5000000) {
                const sanitisedData: UploadFileVars = {
                    data: {
                        file,
                    },
                };
                sendUploadFile({variables: sanitisedData})
                    .then((response) => {
                        enqueueSnackbar('File Upload Success!', {variant: 'success'});
                        const fileUrl = response.data!.uploadFile;
                        handleUploadDocument(fileUrl, document, memberDoc);
                    })
                    .catch(() => {
                        enqueueSnackbar('File Upload Failed!', {variant: 'error'});
                    });
            } else {
                enqueueSnackbar('Document Too Large. 5MB limit.', {variant: 'error'});
            }
        }
    };

    const handleUploadDocument = (newDocumentUrl: string, document: WcvDocument, memberDoc?: WcvDocument) => {
        if (memberDoc) {
            const sanitisedData: UpdateDocumentInput = {
                data: {
                    __typename: undefined,
                    user: undefined,
                    targetResource: undefined,
                    targetGroup: undefined,
                    targetGroupMember: undefined,
                    completed_documents: undefined,
                    overdueGroupMembers: undefined,

                    id: memberDoc.id,
                    url: newDocumentUrl,
                    verified: false,
                }
            };

            sendUpdateDocument({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Document Updated.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Update Document Failed!', {variant: 'error'});
                });

        } else {
            const sanitisedData: CreateDocumentInput = {
                data: {
                    __typename: undefined,
                    id: undefined,
                    completed_documents: undefined,
                    overdueGroupMembers: undefined,
                    user_id: meData.data!.me!.id!,
                    type: WcvType.GROUP_MEMBER,
                    type_id: groupMemberId? groupMemberId : -1, //TODO: fix this. The list will need to passthe member id if we have multiple members on one list.
                    parent_document_id: document.id,
                    title: document.title,
                    description: document.description,
                    available_for_download: false,
                    has_completion_date: false,
                    verified: false,
                    url: newDocumentUrl,
                    tour_role: -1,
                }
            };

            sendCreateDocument({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Document Saved.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Save Document Failed!', {variant: 'error'});
                });
        }
    };

    return (
        <div {...rest} className={clsx(classes.root, className)}>
            <Card>
                <CardHeader title={'Documents'}/>
                <Divider/>
                <CardContent className={classes.content}>
                    <PerfectScrollbar>
                        <div className={classes.inner}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell style={{width: 125}}>Complete By</TableCell>
                                        <TableCell style={{width: 100}}>Completed?</TableCell>
                                        {isAdmin && (<TableCell style={{width: 100}}>Verified?</TableCell>)}
                                        <TableCell style={{width: 250}}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {documents.map(document => {
                                        const memberDoc = document.completed_documents && document.completed_documents.find(doc => doc.type === WcvType.GROUP_MEMBER && doc.type_id === groupMemberId);
                                        const overdue = document.has_completion_date && (!memberDoc && moment(document.completion_date).isSameOrBefore(new Date()));

                                        return (
                                            <TableRow hover key={document.id}>
                                                <TableCell>{document.title}</TableCell>
                                                <TableCell>{document.description}</TableCell>
                                                <TableCell>
                                                    {document.has_completion_date && formatDate(document.completion_date)}
                                                    {overdue && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="overdue"
                                                              className={classes.chipOverdue} size="small"/>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {memberDoc ? (
                                                        <Link href={getHost() + memberDoc.url} target="_blank">
                                                            <Button variant="contained"
                                                                    className={classes.linkButton}>View</Button>
                                                        </Link>
                                                    ) : 'no'}
                                                </TableCell>
                                                {isAdmin && (<TableCell>
                                                    {memberDoc ?
                                                        memberDoc.verified ? 'yes' :
                                                            'no' + isAdmin ? (
                                                                <Button variant="contained"
                                                                        className={classes.linkButton}
                                                                        onClick={() => handleVerifyDocument(memberDoc)}
                                                                >Verify</Button>
                                                            ) : ''
                                                        : '-'}
                                                </TableCell>)}
                                                <TableCell>
                                                    <Link href={getHost() + document.url} target="_blank">
                                                        <Button variant="contained"
                                                                className={classes.linkButton}>Download</Button>
                                                    </Link>

                                                    <form style={{display: 'inline'}}>

                                                        {document.has_completion_date && (
                                                            <label htmlFor={'file_upload[' + document.id + ']'}>
                                                                <Button component="span" variant="contained"
                                                                        className={classes.linkButton}>Upload</Button>
                                                            </label>
                                                        )}

                                                        <input
                                                            accept="application/pdf,image/*"
                                                            style={{display: 'none'}}
                                                            id={'file_upload[' + document.id + ']'}
                                                            multiple
                                                            type="file"
                                                            onChange={event => handleFileChange(event, document, memberDoc)}
                                                            value={''}
                                                        />
                                                    </form>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </PerfectScrollbar>
                </CardContent>

                {paginatorInfo && rowsPerPage && (
                    <CardActions className={classes.actions}>
                        <TablePagination
                            component="div"
                            count={paginatorInfo.total}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            page={paginatorInfo.currentPage - 1}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </CardActions>
                )}

            </Card>
            <TableEditBar selected={[]}/>
        </div>
    );
};

export default Results;
